/* Tap-to-answer page for the pour-conditions warning text. No login — the link
   in the SMS is the credential. Exists so a customer whose plan blocks replies
   to our sender number can still answer in one tap. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, PhoneCall, Loader2, AlertTriangle, Droplets } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPTIONS = [
  { code: "go_ahead", label: "Go ahead", blurb: "Deliver as planned", Icon: CheckCircle2, tone: "emerald" },
  { code: "delay", label: "Delay it", blurb: "Hold the load, call me to rebook", Icon: Clock, tone: "red" },
  { code: "call_me", label: "Call me", blurb: "I'd like to talk it through", Icon: PhoneCall, tone: "amber" },
];

const TONE = {
  emerald: "border-emerald-500 text-emerald-300 hover:bg-emerald-500/15",
  red: "border-red-500 text-red-300 hover:bg-red-500/15",
  amber: "border-amber-500 text-amber-300 hover:bg-amber-500/15",
};

export default function PourResponse() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/pour/${token}`)
      .then((r) => { setData(r.data); if (r.data.reply) setDone(r.data.reply); })
      .catch(() => setError("This link has expired or is no longer valid."));
  }, [token]);

  const choose = async (code) => {
    setSending(code);
    try {
      const { data: res } = await axios.post(`${API}/public/pour/${token}`, { choice: code });
      setDone({ code, label: res.label, ack: res.ack });
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save your answer — please call us instead.");
    } finally {
      setSending(null);
    }
  };

  if (error) {
    return (
      <Shell>
        <div className="border border-red-500/60 bg-red-500/10 p-5 text-center" data-testid="pour-error">
          <AlertTriangle className="w-7 h-7 text-red-400 mx-auto mb-2" />
          <div className="text-sm text-red-200">{error}</div>
        </div>
      </Shell>
    );
  }

  if (!data) {
    return <Shell><div className="flex items-center justify-center py-16"><Loader2 className="w-7 h-7 text-[#F57C00] animate-spin" /></div></Shell>;
  }

  return (
    <Shell supplier={data.supplier}>
      <div data-testid="pour-response-page">
        <div className="border-l-4 border-[#F57C00] bg-[#F57C00]/10 px-4 py-3">
          <div className="flex items-start gap-2">
            <Droplets className="w-5 h-5 text-[#F57C00] shrink-0 mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#F57C00] font-bold">Pour conditions warning</div>
              <div className="text-sm text-zinc-200 mt-1 leading-relaxed">
                Today's weather is hard on fresh concrete. Plastic shrinkage cracking is a real risk —
                curing compound or aliphatic alcohol is recommended on site.
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 border border-zinc-800 divide-y divide-zinc-800" data-testid="pour-order-summary">
          {[
            ["Order", `${data.quantity_m3 ?? "—"} m³ · ${data.mix_code || "—"}`],
            ["When", `${data.date || "—"} ${data.time_required || ""}`.trim()],
            ["Site", data.site || "—"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-baseline gap-3 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 w-16 shrink-0">{k}</div>
              <div className="text-sm text-zinc-100 font-semibold break-words">{v}</div>
            </div>
          ))}
        </div>

        {(data.reasons || []).length > 0 && (
          <ul className="mt-3 space-y-1" data-testid="pour-reasons">
            {data.reasons.map((r, i) => (
              <li key={i} className="text-xs text-amber-300 flex gap-2"><span className="text-amber-500">•</span>{r}</li>
            ))}
          </ul>
        )}

        {done ? (
          <div className="mt-6 border border-emerald-500/60 bg-emerald-500/10 p-5 text-center" data-testid="pour-done">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <div className="text-lg font-black uppercase tracking-wide text-emerald-300" data-testid="pour-done-label">{done.label}</div>
            <div className="text-sm text-zinc-300 mt-2">{done.ack || "Thanks — dispatch has been told."}</div>
            {data.phone && <div className="text-xs text-zinc-500 mt-3">Need to change it? Call us on <a className="text-[#F57C00]" href={`tel:${data.phone}`}>{data.phone}</a></div>}
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">What would you like us to do?</div>
            {OPTIONS.map((o) => (
              <button
                key={o.code}
                type="button"
                disabled={!!sending || data.closed}
                onClick={() => choose(o.code)}
                data-testid={`pour-choice-${o.code}`}
                className={`w-full border-2 bg-transparent px-4 py-4 flex items-center gap-3 text-left transition-colors disabled:opacity-50 ${TONE[o.tone]}`}
              >
                {sending === o.code ? <Loader2 className="w-6 h-6 animate-spin shrink-0" /> : <o.Icon className="w-6 h-6 shrink-0" />}
                <div className="min-w-0">
                  <div className="font-black uppercase tracking-wide">{o.label}</div>
                  <div className="text-xs text-zinc-400">{o.blurb}</div>
                </div>
              </button>
            ))}
            {data.closed && <div className="text-xs text-zinc-500">This order is already closed — please call us if anything's changed.</div>}
            {data.phone && (
              <a href={`tel:${data.phone}`} data-testid="pour-call-link"
                className="block text-center text-xs text-zinc-400 pt-2">Or call us on <span className="text-[#F57C00]">{data.phone}</span></a>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}

const Shell = ({ supplier, children }) => (
  <div className="min-h-screen bg-[#0A0A0B] text-zinc-100 px-4 py-8">
    <div className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="text-xl font-black uppercase tracking-[0.18em]">{supplier || "ArmorBatch"}</div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Concrete delivery</div>
      </div>
      {children}
      <div className="text-center text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-8">Powered by ArmorBatch</div>
    </div>
  </div>
);
